/**
 * Default Orchestrator
 * Manages all default modules with static imports (Phase 2)
 *
 * Transition loads immediately and blocks, then all other modules load in parallel
 */
import config from '@config';
import { init as transitionInit } from './functions/transition.ts';
import { init as smoothScrollInit } from './functions/smooth-scroll.ts';
import { init as navbarInit } from './functions/navbar/navbar.ts';
import { init as accordionInit } from './functions/accordion.ts';
import { init as tocInit } from './functions/toc/toc.ts';
import { init as paginationInit } from './functions/pagination.ts';
import { init as comparisonInit } from './functions/comparison.ts';
import { init as tabsInit } from './functions/tabs.ts';
import { init as formInit } from './functions/form/form.ts';
import { init as linkPrefetchInit } from './functions/link-prefetch.ts';

const CONFIG_ROOT = 'default';

export async function init() {
  const cleanup = { destroyFunctions: [] as Array<() => void> };
  const moduleConfig = config[CONFIG_ROOT];

  // Phase 1: Transition must load and init first (blocks other modules)
  // Note: Transition handles IX3 wait internally before triggering enter animation
  try {
    const transitionResult = await transitionInit();
    if (transitionResult?.destroy) {
      cleanup.destroyFunctions.push(transitionResult.destroy);
    }
  } catch (error) {
    console.error('[default] Error initializing transition:', error);
  }

  // Phase 2: All other modules run in parallel after transition
  const modulePromises = [
    smoothScrollInit(moduleConfig['smooth-scroll'] as any),
    navbarInit(moduleConfig.navbar),
    accordionInit(moduleConfig.accordion),
    tocInit(),
    paginationInit(moduleConfig.pagination as any),
    comparisonInit(moduleConfig.comparison as any),
    tabsInit(moduleConfig.tabs as any),
    formInit(),
    linkPrefetchInit(),
  ];

  const results = await Promise.allSettled(modulePromises);

  // Collect destroy functions from successful inits
  results.forEach((result) => {
    if (result.status === 'fulfilled' && result.value?.destroy) {
      cleanup.destroyFunctions.push(result.value.destroy);
    }
  });

  // Log summary
  const succeeded = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.length - succeeded;
  if (failed > 0) {
    console.warn(
      `[default] ${succeeded + 1}/${results.length + 1} modules loaded successfully. ${failed} failed but won't affect other modules.`
    );
  }

  return {
    result: 'default initialized',
    destroy: () => {
      cleanup.destroyFunctions.forEach((destroyFn) => {
        try {
          destroyFn();
        } catch (error) {
          console.error('[default] Error during cleanup:', error);
        }
      });
      cleanup.destroyFunctions.length = 0;
    },
  };
}
